import { createClient } from '@supabase/supabase-js';
import { Drill, DrillStep, Practice, Team } from '../types';
import { handleError } from '../utils/errorHandling';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
export const supabase = createClient(supabaseUrl, supabaseKey);

/**
 * Team-related database operations
 */
export const TeamService = {
  async getTeams(): Promise<Team[]> {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      handleError(error, 'Failed to fetch teams');
      return [];
    }
  },
  
  async updateTeam(team: Partial<Team> & { id: string }): Promise<void> {
    try {
      const { error } = await supabase
        .from('teams')
        .update(team)
        .eq('id', team.id);
      
      if (error) throw error;
    } catch (error) {
      handleError(error, 'Failed to update team');
    }
  }
};

/**
 * Practice-related database operations
 */
export const PracticeService = {
  async getPractices(teamId: string): Promise<Practice[]> {
    try {
      if (!teamId) {
        console.error('[PracticeService] No team ID provided for fetching practices');
        return [];
      }
      
      console.log(`[PracticeService] Fetching practices for team ID: ${teamId}`);
      
      const { data, error } = await supabase
        .from('practices')
        .select('*')
        .eq('team_id', teamId)
        .order('date', { ascending: true });
      
      if (error) throw error;
      
      // Double-check we're only getting practices for the requested team
      const filteredPractices = data.filter(practice => practice.team_id === teamId);
      console.log(`[PracticeService] Found ${filteredPractices.length} practices for team ${teamId}`);
      
      return filteredPractices;
    } catch (error) {
      handleError(error, 'Failed to fetch practices');
      return [];
    }
  },
  
  async createPractice(practice: Omit<Practice, 'id'>): Promise<Practice | null> {
    try {
      if (!practice.team_id) {
        console.error('[PracticeService] No team ID provided for creating practice');
        return null;
      }
      
      console.log(`[PracticeService] Creating practice for team ID: ${practice.team_id}`);
      
      const { data, error } = await supabase
        .from('practices')
        .insert([practice])
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      handleError(error, 'Failed to create practice');
      return null;
    }
  },
  
  async updatePractice(practice: Practice): Promise<Practice | null> {
    try {
      console.log(`[PracticeService] Updating practice ID: ${practice.id} for team ID: ${practice.team_id}`);
      
      // First verify the practice exists and belongs to the team
      const { data: existingPractice, error: fetchError } = await supabase
        .from('practices')
        .select('*')
        .eq('id', practice.id)
        .eq('team_id', practice.team_id)
        .single();
        
      if (fetchError) {
        console.error('[PracticeService] Error verifying practice:', fetchError);
        throw fetchError;
      }
      
      if (!existingPractice) {
        console.error('[PracticeService] Practice not found or doesn\'t belong to team');
        throw new Error('Practice not found or doesn\'t belong to team');
      }
      
      // Then update the practice
      const { data, error } = await supabase
        .from('practices')
        .update({
          date: practice.date,
          start_time: practice.start_time,
          end_time: practice.end_time,
          location: practice.location,
          theme: practice.theme
        })
        .eq('id', practice.id)
        .eq('team_id', practice.team_id) // Add team_id check for safety
        .select()
        .single(); // Return the updated practice
      
      if (error) throw error;
      
      // Clear team cache
      const cacheClearEvent = new CustomEvent('clear-team-cache', {
        detail: { teamId: practice.team_id }
      });
      window.dispatchEvent(cacheClearEvent);
      
      return data;
    } catch (error) {
      handleError(error, 'Failed to update practice');
      return null;
    }
  },
  
  async deletePractice(practiceId: string): Promise<void> {
    try {
      console.log(`[PracticeService] Deleting practice ID: ${practiceId}`);
      
      const { error } = await supabase
        .from('practices')
        .delete()
        .eq('id', practiceId);
      
      if (error) throw error;
    } catch (error) {
      handleError(error, 'Failed to delete practice');
    }
  }
};

/**
 * Drill-related database operations
 */
export const DrillService = {
  async getDrills(practiceId: string): Promise<Drill[]> {
    try {
      const { data: drills, error } = await supabase
        .from('drills')
        .select(`
          *,
          drill_steps (
            *,
            coaching_points (
              *
            )
          )
        `)
        .eq('practice_id', practiceId)
        .order('order_index');

      if (error) throw error;

      return drills.map(drill => ({
        ...drill,
        position_groups: drill.position_groups || ['ALL'],
        intensity_level: drill.intensity_level,
        steps: drill.drill_steps?.map(step => ({
          ...step,
          coaching_points: step.coaching_points?.map(cp => cp.point) || [],
          equipment: step.equipment || '',
          video_url: step.video_url || ''
        })) || []
      }));
    } catch (error) {
      handleError(error, 'Failed to fetch drills');
      return [];
    }
  },
  
  async createDrill(drill: Omit<Drill, 'id'>, steps: DrillStep[] = []): Promise<string | null> {
    try {
      // Create base drill object with required fields only
      const drillObject: any = {
        practice_id: drill.practice_id,
        name: drill.name,
        duration: drill.duration,
        description: drill.description || '',
        focus_area: drill.focus_area,
        order_index: drill.order_index,
        intensity_level: (drill as any).intensity_level ?? null // NULL for water breaks, or the provided value
      };
      
      // Insert the drill first
      const { data: drillData, error: drillError } = await supabase
        .from('drills')
        .insert([drillObject])
        .select('id')
        .single();
      
      if (drillError) throw drillError;
      
      const drillId = drillData.id;
      
      // Create position groups
      const positionGroups = drill.position_groups || ['ALL'];
      
      // Create steps with equipment and video_url
      if (steps.length > 0) {
        const stepObjects = steps.map((step, index) => ({
          drill_id: drillId,
          name: step.name,
          setup: step.setup || '',
          execution: step.execution || '',
          equipment: step.equipment || '',
          video_url: step.video_url || '',
          order_index: index,
          duration: step.duration || null,
          description: step.description || ''
        }));

        const { error: stepsError } = await supabase
          .from('drill_steps')
          .insert(stepObjects);

        if (stepsError) throw stepsError;
      }
      
      return drillId;
    } catch (error) {
      handleError(error, 'Failed to create drill');
      return null;
    }
  },
  
  async deleteDrill(drillId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('drills')
        .delete()
        .eq('id', drillId);
      
      if (error) throw error;
    } catch (error) {
      handleError(error, 'Failed to delete drill');
    }
  },
  
  async updateDrillOrder(drills: { id: string; order_index: number }[]): Promise<void> {
    try {
      console.log(`[DrillService] Updating order for ${drills.length} drills`);
      
      // Update each drill individually with only the order_index field
      for (const drill of drills) {
        console.log(`[DrillService] Setting drill ${drill.id} to order_index ${drill.order_index}`);
        
        const { error } = await supabase
          .from('drills')
          .update({ order_index: drill.order_index })
          .eq('id', drill.id);
        
        if (error) {
          console.error(`[DrillService] Error updating drill ${drill.id}:`, error);
          throw error;
        }
      }
    } catch (error) {
      console.error('[DrillService] Error updating drill order:', error);
      handleError(error, 'Failed to update drill order');
    }
  },
  
  async clearDrills(practiceId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('drills')
        .delete()
        .eq('practice_id', practiceId);
      
      if (error) throw error;
    } catch (error) {
      handleError(error, 'Failed to clear drills');
    }
  }
}; 